import { useMemo, useState } from 'react';
import { Info, AlertTriangle, Loader2 } from 'lucide-react';
import {
  AreaChart,
  Area,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
  TooltipProps,
} from 'recharts';
import { format } from 'date-fns';

interface ForecastDataPoint {
  time: string;
  timestamp: number;
  expected: number; // Expected energy in kWh
  best: number; // Best case in kWh
  worst: number; // Worst case in kWh
  bandHeight?: number; // best - worst for confidence band
  confidence_upper?: number; // for chart band
  confidence_lower?: number; // for chart band
}

/** 3-layer model: inner/outer cone bounds (visual only; no math/API changes) */
interface LayerConePoint extends ForecastDataPoint {
  /** Inner variance cone (normal variability) — narrow, grows over time */
  inner_lower: number;
  inner_upper: number;
  inner_height: number;
  /** Outer uncertainty cone (divergent outcomes) — only after horizon */
  outer_lower: number;
  outer_upper: number;
  outer_height: number;
}

/** Chart point with night background and directional expected-line segments (rest vs charging). */
interface LayerConePointWithNight extends LayerConePoint {
  nightBackground: number | null;
  /** Expected value when slope non-positive; null when charging (for 2.5px line). */
  expectedRest: number | null;
  /** Expected value when slope positive (charging); null otherwise (for 3px line). */
  expectedCharging: number | null;
}

interface EnergyOutlookProps {
  /** Current energy at sunrise (expected scenario) */
  atSunriseExpected: number;
  /** Current energy at sunrise (best case) */
  atSunriseBest: number;
  /** Current energy at sunrise (worst case) */
  atSunriseWorst: number;
  /** Current energy percentage at sunrise */
  atSunrisePercent?: number;
  /** Net draw in kW */
  netDrawKw: number;
  /** Time to reserve breach (null if not projected) */
  timeToReserve: string | null;
  /** Time to sunrise */
  timeToSunrise: string;
  /** Reserve threshold in kWh */
  reserveKwh: number;
  /** Battery capacity in kWh (Y-axis max = capacity; tooltip clamped to [0, capacity]) */
  batteryCapacityKwh: number;
  /** Forecast chart data */
  chartData: ForecastDataPoint[];
  /** Sunrise timestamp */
  sunriseTime: string;
  /** Sunset timestamp */
  sunsetTime: string;
  /** Confidence level */
  confidenceLevel: 'high' | 'medium' | 'low';
  /** Confidence percentage */
  confidencePercent: number;
  /** Confidence boundary time (when forecast degrades) */
  confidenceBoundaryTime?: string;
  /** Depletion time for expected scenario */
  depletionTimeExpected: string | null;
  /** Depletion time for best case */
  depletionTimeBest: string | null;
  /** Depletion time for worst case */
  depletionTimeWorst: string | null;
  /** Loading state */
  loading?: boolean;
  /** Error state */
  error?: string | null;
  /** Optional retry callback (e.g. for fetch failure) */
  onRetry?: () => void;
  /** Similar-day forecast: chart data (same shape as chartData) */
  similarDayChartData?: ForecastDataPoint[];
  /** Similar-day: number of historical days used */
  similarDaySamplesUsed?: number;
  /** Similar-day: confidence label (e.g. high, medium) */
  similarDayConfidence?: string;
  /** Why this forecast (expandable section; from forecast-runway why block) */
  forecastWhy?: {
    pvDriver: string;
    loadDriver: string;
    confidenceDriver: string;
    compDay?: { date: string; similarity?: number; matchedOn: string[] };
    todayCorrection?: { pvRatio: number; loadTrend: string };
  } | null;
}

/** Round minutes down to nearest quarter hour (0, 15, 30, 45) */
function floorToQuarterHour(date: Date): Date {
  const d = new Date(date);
  const mins = d.getMinutes();
  d.setMinutes(Math.floor(mins / 15) * 15, 0, 0);
  return d;
}

/** Format X-axis label as quarter-hour exact: "HH:mm" → "h:mm a" rounded down to :00, :15, :30, :45 */
function formatXAxisTick(timeStr: string): string {
  const [h, m] = timeStr.split(':').map((n) => parseInt(n, 10));
  if (Number.isNaN(h) || Number.isNaN(m)) return timeStr;
  const d = new Date();
  d.setHours(h, m, 0, 0);
  const q = floorToQuarterHour(d);
  return format(q, 'h:mm a');
}

/** Ticks at 15-min boundaries (:00, :15, :30, :45), or 30-min if data has no quarter-hour points. */
function getXAxisTicks(data: ForecastDataPoint[]): string[] {
  if (data.length === 0) return [];
  const onQuarterHour = data.filter((d) => {
    const mins = new Date(d.timestamp).getMinutes();
    return mins === 0 || mins === 15 || mins === 30 || mins === 45;
  });
  if (onQuarterHour.length >= 4) {
    return onQuarterHour.map((d) => d.time);
  }
  const onHalfHour = data.filter((d) => {
    const mins = new Date(d.timestamp).getMinutes();
    return mins === 0 || mins === 30;
  });
  if (onHalfHour.length >= 2) {
    return onHalfHour.map((d) => d.time);
  }
  // Fallback: one tick per 15-min slot using closest data point (preserves spacing intent)
  const firstTs = data[0].timestamp;
  const lastTs = data[data.length - 1].timestamp;
  const startQ = floorToQuarterHour(new Date(firstTs)).getTime();
  const ticks: string[] = [];
  for (let t = startQ; t <= lastTs; t += 15 * 60 * 1000) {
    const closest = data.reduce((best, d) =>
      Math.abs(d.timestamp - t) < Math.abs(best.timestamp - t) ? d : best
    );
    const timeStr = closest.time;
    if (!ticks.includes(timeStr)) ticks.push(timeStr);
  }
  return ticks;
}

/** Custom tooltip: time, energy, net draw, reserve crossed. Display-only clamp: energy in [0, batteryCapacityKwh]. */
function ForecastChartTooltip(
  props: TooltipProps<number, string> & { reserveKwh?: number; netDrawKw?: number; batteryCapacityKwh?: number }
) {
  const { active, payload, reserveKwh = 0, netDrawKw = 0, batteryCapacityKwh = 100 } = props;
  if (!active || !payload?.[0]?.payload) return null;
  const data = payload[0].payload as ForecastDataPoint;
  const energyDisplay = Math.max(0, Math.min(batteryCapacityKwh, data.expected));
  const crossesReserve = data.expected < reserveKwh;
  return (
    <div
      className="rounded-md border border-border bg-wt-bg-tertiary px-3 py-2 shadow-lg"
      style={{ fontFamily: 'var(--font-sans)' }}
    >
      <div className="font-sans text-sm uppercase tracking-wider text-wt-text-secondary mb-1 font-medium">
        {format(new Date(data.timestamp), 'MMM d, h:mm a')}
      </div>
      <div className="font-mono text-sm text-foreground space-y-0.5">
        <div>
          <span className="text-wt-text-secondary mr-1.5">Energy:</span>
          <span>{energyDisplay.toFixed(1)} kWh</span>
        </div>
        <div>
          <span className="text-wt-text-secondary mr-1.5">Net draw:</span>
          <span>{netDrawKw >= 0 ? '+' : ''}{netDrawKw.toFixed(1)} kW</span>
        </div>
        {crossesReserve && (
          <div className="text-wt-amber-primary mt-1 text-sm font-medium">Below reserve</div>
        )}
      </div>
    </div>
  );
}

export function EnergyOutlook({
  atSunriseExpected,
  atSunriseBest,
  atSunriseWorst,
  atSunrisePercent,
  netDrawKw,
  timeToReserve,
  timeToSunrise,
  reserveKwh,
  batteryCapacityKwh,
  chartData,
  sunriseTime,
  sunsetTime,
  confidenceLevel,
  confidencePercent,
  confidenceBoundaryTime,
  depletionTimeExpected,
  depletionTimeBest,
  depletionTimeWorst,
  loading = false,
  error = null,
  onRetry,
  similarDayChartData,
  similarDaySamplesUsed = 0,
  similarDayConfidence = 'low',
  forecastWhy = null,
}: EnergyOutlookProps) {
  const [forecastScenario, setForecastScenario] = useState<'runway' | 'similar_day'>('runway');
  const [whyExpanded, setWhyExpanded] = useState(false);
  const hasSimilarDay = (similarDayChartData?.length ?? 0) > 0;
  const effectiveChartData = forecastScenario === 'similar_day' && hasSimilarDay
    ? similarDayChartData!
    : chartData;

  const sunriseTs = sunriseTime ? new Date(sunriseTime).getTime() : NaN;
  const sunsetTs = sunsetTime ? new Date(sunsetTime).getTime() : NaN;
  const nowTs = Date.now();
  const sunriseValid = !Number.isNaN(sunriseTs);
  const sunsetValid = !Number.isNaN(sunsetTs);

  // Format depletion/time strings: never show raw ISO to users
  const formatTimeDisplay = (value: string | null): string => {
    if (!value) return 'Not projected';
    if (value.includes('T')) {
      try {
        return format(new Date(value), 'h:mm a');
      } catch {
        return value;
      }
    }
    return value;
  };
  const depletionExpectedDisplay = formatTimeDisplay(depletionTimeExpected);
  const depletionBestDisplay = formatTimeDisplay(depletionTimeBest);
  const depletionWorstDisplay = formatTimeDisplay(depletionTimeWorst);

  // 3-layer model: expected line (anchor) + inner cone (normal variance) + outer cone (after horizon).
  // Remap existing expected/best/worst only; no forecast math or API changes.
  const OUTER_HORIZON_MS = 3 * 60 * 60 * 1000; // +3h from now
  const chartDataWithBand = useMemo((): LayerConePoint[] => {
    const now = Date.now();
    const outerHorizonTs = confidenceBoundaryTime
      ? new Date(confidenceBoundaryTime).getTime()
      : now + OUTER_HORIZON_MS;
    const startTs = effectiveChartData.length > 0 ? effectiveChartData[0].timestamp : now;
    const endTs = effectiveChartData.length > 0 ? effectiveChartData[effectiveChartData.length - 1].timestamp : now;
    const spanMs = Math.max(1, endTs - now);

    return effectiveChartData.map((d) => {
      const expected = d.expected ?? 0;
      const best = d.best ?? expected;
      const worst = d.worst ?? expected;
      const bandHeight = d.bandHeight ?? Math.max(0, best - worst);

      // Inner cone: narrow band centered on expected, grows gradually from now forward (0 → ~0.5 of full spread).
      const isFuture = d.timestamp > now;
      const growth = isFuture
        ? Math.min(0.6, ((d.timestamp - now) / spanMs) * 0.8)
        : 0;
      const innerHalfUp = (best - expected) * growth;
      const innerHalfDown = (expected - worst) * growth;
      const inner_upper = expected + innerHalfUp;
      const inner_lower = expected - innerHalfDown;
      const inner_height = Math.max(0, inner_upper - inner_lower);

      // Outer cone: full best/worst only after horizon; before that no band (same as expected).
      const showOuter = d.timestamp >= outerHorizonTs;
      const outer_upper = showOuter ? best : expected;
      const outer_lower = showOuter ? worst : expected;
      const outer_height = Math.max(0, outer_upper - outer_lower);

      return {
        ...d,
        bandHeight,
        confidence_upper: best,
        confidence_lower: worst,
        inner_lower,
        inner_upper,
        inner_height,
        outer_lower,
        outer_upper,
        outer_height,
      };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- OUTER_HORIZON_MS is constant; omit to avoid noise
  }, [effectiveChartData, confidenceBoundaryTime]);

  // Optional inflection emphasis: slightly tighten inner cone after first positive slope (sunrise/PV onset).
  const chartDataWithBandAndInflection = useMemo((): LayerConePoint[] => {
    const data = chartDataWithBand;
    if (data.length < 2) return data;
    const firstInflectionIdx = data.findIndex((d, i) => i > 0 && (d.expected ?? 0) > (data[i - 1].expected ?? 0));
    if (firstInflectionIdx < 0) return data;
    const INFLECTION_TIGHTEN_POINTS = 6;
    const TIGHTEN_SCALE = 0.9;
    return data.map((d, i) => {
      const inTightenRange = i >= firstInflectionIdx && i < firstInflectionIdx + INFLECTION_TIGHTEN_POINTS;
      if (!inTightenRange) return d;
      const expected = d.expected ?? 0;
      const inner_upper = d.inner_upper ?? expected;
      const inner_lower = d.inner_lower ?? expected;
      const upperToward = expected + (inner_upper - expected) * TIGHTEN_SCALE;
      const lowerToward = expected - (expected - inner_lower) * TIGHTEN_SCALE;
      return {
        ...d,
        inner_upper: upperToward,
        inner_lower: lowerToward,
        inner_height: Math.max(0, upperToward - lowerToward),
      };
    });
  }, [chartDataWithBand]);

  const xAxisTicks = useMemo(
    () => getXAxisTicks(chartDataWithBand),
    [chartDataWithBand]
  );

  // Chart time range (for sunrise/sunset visibility)
  const chartStartTs = chartDataWithBand.length > 0 ? chartDataWithBand[0].timestamp : 0;
  const chartEndTs = chartDataWithBand.length > 0 ? chartDataWithBand[chartDataWithBand.length - 1].timestamp : 0;
  const chartSpan = chartEndTs - chartStartTs || 1;
  const nowPoint = chartDataWithBand.find(
    (d, i) => {
      const next = chartDataWithBand[i + 1];
      return next && d.timestamp <= nowTs && next.timestamp > nowTs;
    }
  );

  // Day/night segments for background shading (night = sunset → sunrise; day = sunrise → sunset).
  // Use local midnight + local time-of-day so boundaries align with chart timezone.
  // First segment starts at chartStartTs, so if the x-axis starts at night (e.g. 9pm), night shows until sunrise.
  const dayNightSegments = useMemo(() => {
    if (!chartDataWithBand.length || !sunriseValid || !sunsetValid) return [];
    const dayMs = 24 * 60 * 60 * 1000;
    const sunriseDate = new Date(sunriseTs);
    const sunsetDate = new Date(sunsetTs);
    const sunriseTod = sunriseDate.getHours() * 3600000 + sunriseDate.getMinutes() * 60000;
    const sunsetTod = sunsetDate.getHours() * 3600000 + sunsetDate.getMinutes() * 60000;
    const isNight = (ts: number) => {
      const d = new Date(ts);
      const tod = d.getHours() * 3600000 + d.getMinutes() * 60000 + d.getSeconds() * 1000;
      if (sunsetTod > sunriseTod) return tod >= sunsetTod || tod < sunriseTod;
      return tod >= sunsetTod && tod < sunriseTod;
    };
    const boundaries: { ts: number }[] = [];
    let d = new Date(chartStartTs);
    d.setHours(0, 0, 0, 0);
    let localMidnight = d.getTime();
    while (localMidnight < chartEndTs + dayMs) {
      const sunR = localMidnight + sunriseTod;
      const sunS = localMidnight + sunsetTod;
      if (sunR >= chartStartTs && sunR <= chartEndTs) boundaries.push({ ts: sunR });
      if (sunS >= chartStartTs && sunS <= chartEndTs) boundaries.push({ ts: sunS });
      d = new Date(localMidnight + dayMs);
      d.setHours(0, 0, 0, 0);
      localMidnight = d.getTime();
    }
    boundaries.sort((a, b) => a.ts - b.ts);
    const segments: { start: number; end: number; isNight: boolean }[] = [];
    let prev = chartStartTs;
    for (const b of boundaries) {
      if (b.ts <= prev) continue;
      const mid = (prev + b.ts) / 2;
      segments.push({ start: prev, end: b.ts, isNight: isNight(mid) });
      prev = b.ts;
    }
    if (prev < chartEndTs) segments.push({ start: prev, end: chartEndTs, isNight: isNight((prev + chartEndTs) / 2) });
    return segments.filter((s) => s.isNight);
  }, [chartDataWithBand.length, chartStartTs, chartEndTs, sunriseValid, sunsetValid, sunriseTs, sunsetTs]);

  // Y-axis bounded by physical capacity only. Energy charts should map to physical reality; full = top of chart.
  const chartYMax = batteryCapacityKwh;

  // Night background + directional expected line (rest 2.5px, charging 3px). Cap display values at chartYMax so line/cones flatten at top (no clipping artifacts).
  const chartDataWithNightBackground = useMemo((): LayerConePointWithNight[] => {
    const isInNight = (ts: number) =>
      dayNightSegments.some((seg) => ts >= seg.start && ts <= seg.end);
    const data = chartDataWithBandAndInflection;
    const cap = chartYMax;
    return data.map((d, i) => {
      const expected = d.expected ?? 0;
      const slopePositive = i > 0 && expected > (data[i - 1].expected ?? 0);
      const expectedCapped = Math.min(expected, cap);
      const innerUpper = d.inner_upper ?? expected;
      const innerLower = d.inner_lower ?? expected;
      const innerUpperCapped = Math.min(innerUpper, cap);
      const innerLowerCapped = Math.min(innerLower, innerUpperCapped);
      const outerUpper = d.outer_upper ?? expected;
      const outerLower = d.outer_lower ?? expected;
      const outerUpperCapped = Math.min(outerUpper, cap);
      const outerLowerCapped = Math.min(outerLower, outerUpperCapped);
      return {
        ...d,
        inner_lower: innerLowerCapped,
        inner_upper: innerUpperCapped,
        inner_height: Math.max(0, innerUpperCapped - innerLowerCapped),
        outer_lower: outerLowerCapped,
        outer_upper: outerUpperCapped,
        outer_height: Math.max(0, outerUpperCapped - outerLowerCapped),
        nightBackground: isInNight(d.timestamp) ? cap : null,
        expectedRest: slopePositive ? null : expectedCapped,
        expectedCharging: slopePositive ? expectedCapped : null,
      };
    });
  }, [chartDataWithBandAndInflection, dayNightSegments, chartYMax]);

  return (
    <div className="bg-wt-bg-secondary border border-border rounded-[10px] flex flex-col min-h-[320px]">
      {/* Header: title + scenario toggle + confidence */}
      <div className="flex justify-between items-center p-6 pb-4 flex-wrap gap-2">
        <div className="flex items-center gap-3">
          <span className="font-sans text-sm uppercase tracking-[0.05em] text-wt-text-secondary font-medium">
            Energy Outlook
          </span>
          {hasSimilarDay && (
            <div className="flex rounded-md border border-border overflow-hidden">
              <button
                type="button"
                onClick={() => setForecastScenario('runway')}
                className={`px-2.5 py-1 text-sm font-medium transition-colors ${
                  forecastScenario === 'runway'
                    ? 'bg-muted text-foreground'
                    : 'bg-transparent text-muted-foreground hover:text-foreground'
                }`}
              >
                Runway
              </button>
              <button
                type="button"
                onClick={() => setForecastScenario('similar_day')}
                className={`px-2.5 py-1 text-sm font-medium transition-colors ${
                  forecastScenario === 'similar_day'
                    ? 'bg-muted text-foreground'
                    : 'bg-transparent text-muted-foreground hover:text-foreground'
                }`}
              >
                Similar Days
              </button>
            </div>
          )}
        </div>
        <div className="flex items-center gap-2">
          <span className="font-sans text-sm text-wt-text-secondary capitalize">
            {forecastScenario === 'similar_day' && hasSimilarDay
              ? `Based on ${similarDaySamplesUsed} similar days · ${similarDayConfidence}`
              : `${confidenceLevel} Confidence`}
          </span>
          <Info className="w-4 h-4 text-wt-text-secondary" />
        </div>
      </div>

      {/* Why this forecast (Phase 4.2): expandable when forecastWhy is present */}
      {forecastWhy && forecastScenario === 'runway' && (
        <div className="px-6 pb-2">
          <button
            type="button"
            onClick={() => setWhyExpanded((e) => !e)}
            className="font-sans text-sm text-wt-text-secondary hover:text-foreground flex items-center gap-1.5"
          >
            {whyExpanded ? 'Hide' : 'Why this forecast'}
          </button>
          {whyExpanded && (
            <div className="mt-2 p-3 bg-wt-bg-tertiary border border-border rounded-lg font-sans text-sm text-wt-text-secondary space-y-1.5">
              <p><span className="font-medium text-foreground">PV:</span> {forecastWhy.pvDriver}</p>
              <p><span className="font-medium text-foreground">Load:</span> {forecastWhy.loadDriver}</p>
              <p><span className="font-medium text-foreground">Confidence:</span> {forecastWhy.confidenceDriver}</p>
              {forecastWhy.todayCorrection && (
                <p>Today correction: PV ratio {forecastWhy.todayCorrection.pvRatio}; {forecastWhy.todayCorrection.loadTrend}.</p>
              )}
            </div>
          )}
        </div>
      )}

      {/* Chart — content-driven height; scenario cards sit immediately below */}
      <div className="px-6 pb-4 relative overflow-visible">
          <div className="h-[280px] relative overflow-visible">
            {loading ? (
              <div className="h-full flex items-center justify-center">
                <div className="flex flex-col items-center gap-2">
                  <Loader2 className="w-5 h-5 animate-spin text-wt-text-secondary" />
                  <span className="font-sans text-sm text-wt-text-secondary">Loading forecast...</span>
                </div>
              </div>
            ) : error ? (
              <div className="h-full flex items-center justify-center">
                <div className="flex flex-col items-center gap-2 text-center">
                  <AlertTriangle className="w-5 h-5 text-destructive" />
                  <span className="font-sans text-sm text-foreground">{error}</span>
                  {onRetry && (
                    <button
                      type="button"
                      onClick={onRetry}
                      className="font-sans text-sm text-wt-green-primary hover:text-wt-green-muted underline mt-1"
                    >
                      Retry
                    </button>
                  )}
                </div>
              </div>
            ) : effectiveChartData.length === 0 ? (
              <div className="h-full flex items-center justify-center">
                <span className="font-sans text-sm text-wt-text-secondary">No forecast data available</span>
              </div>
            ) : (
              <div className="relative w-full h-full">
              <ResponsiveContainer width="100%" height="100%" key="forecast-chart" style={{ overflow: 'visible' }}>
              <AreaChart
                data={chartDataWithNightBackground}
                margin={{ top: 24, right: 10, bottom: 10, left: 10 }}
                layout="horizontal"
              >
                <defs>
                  {/* Design token: --text-primary (#E5E7EB = rgb 229,231,235). SVG stop-color requires rgba, not hsl(var()). */}
                  <linearGradient id="outerCone" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="rgba(229, 231, 235, 0.06)" />
                    <stop offset="100%" stopColor="rgba(229, 231, 235, 0.04)" />
                  </linearGradient>
                  <linearGradient id="innerCone" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="rgba(229, 231, 235, 0.14)" />
                    <stop offset="100%" stopColor="rgba(229, 231, 235, 0.10)" />
                  </linearGradient>
                </defs>
                {/* Night background: dedicated Area series; render first so it sits behind grid and data */}
                <Area
                  type="monotone"
                  dataKey="nightBackground"
                  stroke="none"
                  fill="hsl(var(--chart-night-surface))"
                  fillOpacity={1}
                  isAnimationActive={false}
                  connectNulls={false}
                />
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border-subtle))" opacity={0.3} />
                <XAxis
                  dataKey="timestamp"
                  type="number"
                  domain={[chartStartTs, chartEndTs]}
                  tickFormatter={(ts: number) => {
                    const timeStr = chartDataWithNightBackground.find((d) => d.timestamp === ts)?.time;
                    return timeStr ? formatXAxisTick(timeStr) : format(new Date(ts), 'h:mm a');
                  }}
                  ticks={xAxisTicks.length > 0 ? chartDataWithNightBackground.filter((d) => xAxisTicks.includes(d.time)).map((d) => d.timestamp) : undefined}
                  tick={{ fill: 'hsl(var(--text-secondary))', fontSize: 10, fontFamily: 'JetBrains Mono' }}
                  stroke="hsl(var(--border-default))"
                  minTickGap={14}
                  interval="preserveStartEnd"
                />
                {/* Energy charts bounded by physical capacity; full = top of chart. Headroom is a modeling concern, not visualization. */}
                <YAxis
                  tick={{ fill: 'hsl(var(--text-secondary))', fontSize: 11, fontFamily: 'JetBrains Mono' }}
                  tickFormatter={(v: number) => String(Math.round(v))}
                  stroke="hsl(var(--border-default))"
                  domain={[0, chartYMax]}
                />
                <Tooltip
                  content={(tooltipProps) => (
                    <ForecastChartTooltip
                      {...tooltipProps}
                      reserveKwh={reserveKwh}
                      netDrawKw={netDrawKw}
                      batteryCapacityKwh={batteryCapacityKwh}
                    />
                  )}
                />
                {/* Layer 3 — Outer cone: drawn first so line stays on top; only after horizon */}
                <Area
                  type="monotone"
                  dataKey="outer_lower"
                  stackId="outer"
                  stroke="none"
                  fill="transparent"
                  isAnimationActive={false}
                />
                <Area
                  type="monotone"
                  dataKey="outer_height"
                  stackId="outer"
                  stroke="none"
                  fill="url(#outerCone)"
                  isAnimationActive={false}
                />
                {/* Layer 2 — Inner cone: narrow band, grows over time */}
                <Area
                  type="monotone"
                  dataKey="inner_lower"
                  stackId="inner"
                  stroke="none"
                  fill="transparent"
                  isAnimationActive={false}
                />
                <Area
                  type="monotone"
                  dataKey="inner_height"
                  stackId="inner"
                  stroke="none"
                  fill="url(#innerCone)"
                  isAnimationActive={false}
                />
                {/* Layer 1 — Expected path: narrative carrier; rest 2.5px, charging phase 3px (direction by geometry, not color) */}
                <Line
                  type="monotone"
                  dataKey="expectedRest"
                  stroke="hsl(var(--text-primary))"
                  strokeWidth={2.5}
                  dot={false}
                  connectNulls={false}
                  isAnimationActive={false}
                />
                <Line
                  type="monotone"
                  dataKey="expectedCharging"
                  stroke="hsl(var(--text-primary))"
                  strokeWidth={3}
                  dot={false}
                  connectNulls={false}
                  isAnimationActive={false}
                />
                {/* Reserve threshold: amber dashed (DESIGN_TOKENS) */}
                <ReferenceLine
                  y={reserveKwh}
                  stroke="hsl(var(--amber-muted))"
                  strokeWidth={2}
                  strokeDasharray="4 4"
                  label={{
                    value: 'Reserve threshold',
                    position: 'right',
                    fill: 'hsl(var(--text-muted))',
                    fontSize: 11,
                  }}
                />
                {/* Now Marker (x = timestamp for numeric X-axis) */}
                {nowPoint && (
                  <ReferenceLine
                    x={nowPoint.timestamp}
                    stroke="hsl(var(--text-primary))"
                    strokeWidth={2}
                    opacity={0.5}
                  />
                )}
                {/* Sunrise marker: muted gold vertical line (design spec) */}
                {sunriseValid && sunriseTs >= chartStartTs && sunriseTs <= chartEndTs && (
                  <ReferenceLine
                    x={sunriseTs}
                    stroke="hsl(var(--solar-gold))"
                    strokeDasharray="4 4"
                    strokeOpacity={0.5}
                    label={{
                      value: 'Sunrise',
                      position: 'top',
                      fill: 'hsl(var(--solar-gold))',
                      fontSize: 10,
                    }}
                  />
                )}
                {/* Sunset marker: muted gold vertical line (design spec) */}
                {sunsetValid && sunsetTs >= chartStartTs && sunsetTs <= chartEndTs && (
                  <ReferenceLine
                    x={sunsetTs}
                    stroke="hsl(var(--solar-gold))"
                    strokeDasharray="4 4"
                    strokeOpacity={0.5}
                    label={{
                      value: 'Sunset',
                      position: 'top',
                      fill: 'hsl(var(--solar-gold))',
                      fontSize: 10,
                    }}
                  />
                )}
              </AreaChart>
            </ResponsiveContainer>
            </div>
            )}
          </div>
      </div>

      {/* Scenario Analysis */}
      <div className="grid grid-cols-3 gap-3 p-4 bg-wt-bg-tertiary border-t border-border">
        {/* Expected Scenario */}
        <div className="p-4 bg-wt-bg-secondary border border-border rounded-lg">
          <label className="font-sans text-sm uppercase tracking-[0.05em] text-wt-text-secondary mb-4 block font-medium">
            Expected
          </label>
          <div className="space-y-3">
            <div>
              <div className="font-sans text-sm uppercase text-wt-text-secondary mb-1 font-medium">At Sunrise</div>
              <div className="font-mono text-base font-semibold text-foreground">
                {atSunriseExpected.toFixed(1)} kWh
              </div>
              {atSunrisePercent !== undefined && (
                <span className="font-mono text-[13px] text-wt-text-secondary ml-1.5">
                  ({Math.round(atSunrisePercent)}%)
                </span>
              )}
            </div>
            <div>
              <div className="font-sans text-sm uppercase text-wt-text-secondary mb-1 font-medium" title="When battery may reach reserve (overnight, before sunrise)">Depletion (overnight)</div>
              <div className="font-mono text-base font-semibold text-foreground">
                {depletionExpectedDisplay}
              </div>
            </div>
          </div>
        </div>

        {/* Best Case Scenario */}
        <div className="p-4 bg-wt-bg-secondary border border-border rounded-lg">
          <label className="font-sans text-sm uppercase tracking-[0.05em] text-wt-text-secondary mb-4 block font-medium">
            Best Case
          </label>
          <div className="space-y-3">
            <div>
              <div className="font-sans text-sm uppercase text-wt-text-secondary mb-1 font-medium">At Sunrise</div>
              <div className="font-mono text-base font-semibold text-foreground">
                {atSunriseBest.toFixed(1)} kWh
              </div>
            </div>
            <div>
              <div className="font-sans text-sm uppercase text-wt-text-secondary mb-1 font-medium" title="When battery may reach reserve (overnight)">Depletion (overnight)</div>
              <div className="font-mono text-base font-semibold text-foreground">
                {depletionBestDisplay}
              </div>
            </div>
          </div>
        </div>

        {/* Worst Case Scenario — same surface as Expected/Best; semantic color on value only */}
        <div className="p-4 bg-wt-bg-secondary border border-border rounded-lg">
          <label className="font-sans text-sm uppercase tracking-[0.05em] text-wt-text-secondary mb-4 block font-medium">
            Worst Case
          </label>
          <div className="space-y-3">
            <div>
              <div className="font-sans text-sm uppercase text-wt-text-secondary mb-1 font-medium">At Sunrise</div>
              <div className="font-mono text-base font-semibold text-destructive">
                {atSunriseWorst.toFixed(1)} kWh
              </div>
            </div>
            <div>
              <div className="font-sans text-sm uppercase text-wt-text-secondary mb-1 font-medium" title="When battery may reach reserve (overnight)">Depletion (overnight)</div>
              <div className="font-mono text-base font-semibold text-destructive">
                {depletionWorstDisplay}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
